import React, { FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext } from '../../../../React/Common/Contexts'
import type { MessageContextType } from '../../../../React/Common/Contexts/MessageContext'
import { Router as RouterLang, DatabaseLanguage } from '../../../../../i18n'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import { getDatabaseSettings, putDatabaseSettings } from '../../../../React/Common/API/Database/DatabaseAPI'
import type { DatabaseSettings, DatabaseSettingsPutRequest, DatabaseSettingsResponse } from '../../../../NonReact/Types/DatabaseSettingsTypes'
import { IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

const DAY_MONDAY: number = 0x01
const DAY_TUESDAY: number = 0x02
const DAY_WEDNESDAY: number = 0x04
const DAY_THURSDAY: number = 0x08
const DAY_FRIDAY: number = 0x10
const DAY_SATURDAY: number = 0x20
const DAY_SUNDAY: number = 0x40

const DATABASE_PAGE_ACTION_BUTTON_CLASS = 'database-page-action-button'
const DATABASE_PAGE_FIELD_CLASS = 'database-page-field'
const DATABASE_PAGE_TIME_FIELD_CLASS = 'database-page-time-field'
const DATABASE_PAGE_TIME_INPUT_CLASS = 'database-page-time-input'
const DATABASE_PAGE_WARNING_CLASS = 'database-page-warning'

type DayDefinition = {
    readonly bit: number
    readonly langKey: string
}

const DAY_DEFINITIONS: readonly DayDefinition[] = [
    { bit: DAY_MONDAY, langKey: DatabaseLanguage.Database_Day_Monday },
    { bit: DAY_TUESDAY, langKey: DatabaseLanguage.Database_Day_Tuesday },
    { bit: DAY_WEDNESDAY, langKey: DatabaseLanguage.Database_Day_Wednesday },
    { bit: DAY_THURSDAY, langKey: DatabaseLanguage.Database_Day_Thursday },
    { bit: DAY_FRIDAY, langKey: DatabaseLanguage.Database_Day_Friday },
    { bit: DAY_SATURDAY, langKey: DatabaseLanguage.Database_Day_Saturday },
    { bit: DAY_SUNDAY, langKey: DatabaseLanguage.Database_Day_Sunday }
]

type DraftSettings = {
    readonly maintenanceIntervalEnabled: boolean
    readonly maintenanceIntervalTimeStart: string
    readonly maintenanceIntervalTimeEnd: string
    readonly maintenanceIntervalDaysOfWeek: number
}

const mapResponseToDraft = (settings: DatabaseSettings): DraftSettings => ({
    maintenanceIntervalEnabled: settings.maintenanceIntervalEnabled,
    maintenanceIntervalTimeStart: settings.maintenanceIntervalTimeStart,
    maintenanceIntervalTimeEnd: settings.maintenanceIntervalTimeEnd,
    maintenanceIntervalDaysOfWeek: settings.maintenanceIntervalDaysOfWeek
})

/**
 * Routed Database settings page.
 */
export const Database: FC = (): ReactElement =>
{
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(DatabaseLanguage.NAME)

    const { displayError, displaySuccessMessage, displayErrorMessage }: MessageContextType = useContext(MessageContext)

    const [settings, setSettings] = useState<Nullable<DatabaseSettings>>(null)
    const [draft, setDraft] = useState<Nullable<DraftSettings>>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [isDirty, setIsDirty] = useState<boolean>(false)

    const canEdit: boolean = settings?.canEdit ?? false
    const isAvailable: boolean = settings?.isAvailable ?? false

    const loadSettings = useCallback((): void =>
    {
        setIsLoading(true)

        getDatabaseSettings()
            .then((response: Nullable<DatabaseSettingsResponse>): void =>
            {
                const data: Nullable<DatabaseSettings> = response?.Data ?? null
                setSettings(data)
                setDraft(isVarNotEmpty(data) ? mapResponseToDraft(data) : null)
                setIsDirty(false)
            })
            .catch((error: Error): void =>
            {
                displayError(error)
            })
            .finally((): void =>
            {
                setIsLoading(false)
            })
    }, [displayError])

    const handleApply = useCallback((): void =>
    {
        if (IsVarEmpty(draft) || !canEdit || !isDirty || isLoading || isSaving) return

        const payload: DatabaseSettingsPutRequest = {
            maintenanceIntervalEnabled: draft.maintenanceIntervalEnabled,
            maintenanceIntervalTimeStart: draft.maintenanceIntervalTimeStart,
            maintenanceIntervalTimeEnd: draft.maintenanceIntervalTimeEnd,
            maintenanceIntervalDaysOfWeek: draft.maintenanceIntervalDaysOfWeek
        }

        setIsSaving(true)

        putDatabaseSettings(payload)
            .then((): void =>
            {
                displaySuccessMessage(language.get(DatabaseLanguage.Database_Save_Success))
                loadSettings()
            })
            .catch((): void =>
            {
                displayErrorMessage(language.get(DatabaseLanguage.Database_Save_Error))
            })
            .finally((): void =>
            {
                setIsSaving(false)
            })
    }, [canEdit, displayErrorMessage, displaySuccessMessage, draft, isDirty, isLoading, isSaving, language, loadSettings])

    const handleCancel = useCallback((): void =>
    {
        loadSettings()
    }, [loadSettings])

    const handleRefresh = useCallback((): void =>
    {
        loadSettings()
    }, [loadSettings])

    const updateDraft = useCallback((patch: Partial<DraftSettings>): void =>
    {
        setDraft(
            (current: Nullable<DraftSettings>): Nullable<DraftSettings> =>
                IsVarEmpty(current) ? current : { ...current, ...patch }
        )
        setIsDirty(true)
    }, [])

    const handleToggleEnabled = useCallback((checked: boolean): void =>
    {
        updateDraft({ maintenanceIntervalEnabled: checked })
    }, [updateDraft])

    const handleTimeStartChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void =>
    {
        updateDraft({ maintenanceIntervalTimeStart: event.target.value })
    }, [updateDraft])

    const handleTimeEndChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void =>
    {
        updateDraft({ maintenanceIntervalTimeEnd: event.target.value })
    }, [updateDraft])

    const handleToggleDay = useCallback((bit: number): void =>
    {
        if (!canEdit) return

        setDraft(
            (current: Nullable<DraftSettings>): Nullable<DraftSettings> =>
            {
                if (IsVarEmpty(current)) return current

                const nextDays: number = current.maintenanceIntervalDaysOfWeek ^ bit
                return { ...current, maintenanceIntervalDaysOfWeek: nextDays }
            }
        )
        setIsDirty(true)
    }, [canEdit])

    const isIntervalFieldsEnabled: boolean = canEdit && (draft?.maintenanceIntervalEnabled ?? false)

    const isApplyEnabled: boolean = canEdit && isDirty && !isLoading && !isSaving

    useEffect((): void =>
    {
        loadSettings()
    }, [loadSettings])

    return (
        <div className='database-page admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_database} />

            {isVarNotEmpty(settings) && !isAvailable ? (
                <div className={DATABASE_PAGE_WARNING_CLASS} data-testid='database-unavailable-warning'>
                    {language.get(DatabaseLanguage.Database_Warning_Unavailable)}
                </div>
            ) : null}

            {isVarNotEmpty(settings) && isAvailable && !canEdit ? (
                <div className={DATABASE_PAGE_WARNING_CLASS} data-testid='database-readonly-warning'>
                    {language.get(DatabaseLanguage.Database_Warning_ReadOnly)}
                </div>
            ) : null}

            {isVarNotEmpty(settings) && isAvailable && isVarNotEmpty(draft) ? (
                <div className='database-page-form' data-testid='database-form'>
                    <div className='database-page-section'>
                        <div className='database-page-section-title'>
                            {language.get(DatabaseLanguage.Database_Section_MaintenanceInterval_Title)}
                        </div>
                        <div className='database-page-section-description'>
                            {language.get(DatabaseLanguage.Database_Section_MaintenanceInterval_Description)}
                        </div>

                        <div className={DATABASE_PAGE_FIELD_CLASS} data-testid='database-enabled-field'>
                            <RouterCheckbox
                                labelLeft={language.get(DatabaseLanguage.Database_Field_MaintenanceInterval_Enabled)}
                                isChecked={draft.maintenanceIntervalEnabled}
                                onChange={handleToggleEnabled}
                                disabled={!canEdit || isLoading || isSaving}
                            />
                        </div>

                        <div className='database-page-time-row'>
                            <div className={DATABASE_PAGE_TIME_FIELD_CLASS} data-testid='database-time-start-field'>
                                <label
                                    className={`database-page-field-label${!isIntervalFieldsEnabled ? ' database-page-field-label--disabled' : EMPTY_STRING}`}
                                >
                                    {language.get(DatabaseLanguage.Database_Field_MaintenanceInterval_TimeStart)}
                                </label>
                                <input
                                    type='time'
                                    className={DATABASE_PAGE_TIME_INPUT_CLASS}
                                    value={draft.maintenanceIntervalTimeStart}
                                    disabled={!isIntervalFieldsEnabled || isLoading || isSaving}
                                    onChange={handleTimeStartChange}
                                    data-testid='database-time-start-input'
                                />
                            </div>

                            <div className={DATABASE_PAGE_TIME_FIELD_CLASS} data-testid='database-time-end-field'>
                                <label
                                    className={`database-page-field-label${!isIntervalFieldsEnabled ? ' database-page-field-label--disabled' : EMPTY_STRING}`}
                                >
                                    {language.get(DatabaseLanguage.Database_Field_MaintenanceInterval_TimeEnd)}
                                </label>
                                <input
                                    type='time'
                                    className={DATABASE_PAGE_TIME_INPUT_CLASS}
                                    value={draft.maintenanceIntervalTimeEnd}
                                    disabled={!isIntervalFieldsEnabled || isLoading || isSaving}
                                    onChange={handleTimeEndChange}
                                    data-testid='database-time-end-input'
                                />
                            </div>
                        </div>

                        <div className={DATABASE_PAGE_FIELD_CLASS} data-testid='database-days-field'>
                            <span
                                className={`database-page-field-label${!isIntervalFieldsEnabled ? ' database-page-field-label--disabled' : EMPTY_STRING}`}
                            >
                                {language.get(DatabaseLanguage.Database_Field_MaintenanceInterval_DaysOfWeek)}
                            </span>
                            <div className='database-page-days-row'>
                                {DAY_DEFINITIONS.map(
                                    (day: DayDefinition): ReactElement =>
                                    {
                                        const isActive: boolean =
                                            (draft.maintenanceIntervalDaysOfWeek & day.bit) !== 0
                                        const isDisabled: boolean = !isIntervalFieldsEnabled || isLoading || isSaving

                                        return (
                                            <button
                                                key={day.bit}
                                                type='button'
                                                className={[
                                                    'database-page-day-toggle',
                                                    isActive ? 'database-page-day-toggle--active' : EMPTY_STRING,
                                                    isDisabled ? 'database-page-day-toggle--disabled' : EMPTY_STRING
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                disabled={isDisabled}
                                                onClick={(): void => handleToggleDay(day.bit)}
                                                data-testid={`database-day-toggle-${day.bit}`}
                                            >
                                                {language.get(day.langKey)}
                                            </button>
                                        )
                                    }
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='database-page-actions'>
                        {isDirty ? (
                            <IconButton
                                customClassName={DATABASE_PAGE_ACTION_BUTTON_CLASS}
                                iconName={IconName.CHECK}
                                type={IconButtonType.BUTTON}
                                title={language.get(DatabaseLanguage.Database_Action_Apply)}
                                onClick={handleApply}
                                isEnabled={isApplyEnabled}
                            />
                        ) : null}
                        {isDirty ? (
                            <IconButton
                                customClassName={DATABASE_PAGE_ACTION_BUTTON_CLASS}
                                iconName={IconName.RESTORE_FROM_TRASH}
                                type={IconButtonType.BUTTON}
                                title={language.get(DatabaseLanguage.Database_Action_Cancel)}
                                onClick={handleCancel}
                                isEnabled={!isLoading && !isSaving}
                            />
                        ) : null}
                        {!isDirty ? (
                            <IconButton
                                customClassName={DATABASE_PAGE_ACTION_BUTTON_CLASS}
                                iconName={IconName.REFRESH}
                                type={IconButtonType.BUTTON}
                                title={language.get(DatabaseLanguage.Database_Action_Refresh)}
                                onClick={handleRefresh}
                                isEnabled={!isLoading && !isSaving}
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    )
}
